/**
 * Read-side enum tolerance that STILL REPORTS (AMP-636).
 *
 * A generated enum mirrors a Django `TextChoices`, but `choices` is not a DB
 * constraint, so a column can legitimately hold a value outside the declared
 * members — `Contract.type` is observed as `''` at runtime (see
 * `services/api/main/tests/schema_exemptions.py`, AMP-619). Widening such a
 * field to `z.string()` stops the raw-tier fallback, but it also deletes the
 * only detector we had: after the widening, genuine corruption parses cleanly
 * and nobody ever hears about it.
 *
 * {@link tolerantEnum} keeps the tolerance and restores the detector. The value
 * passes through unchanged — no coercion to a placeholder, so the UI still
 * renders what the server actually stored — and the fallback branch reports the
 * unknown member to Sentry.
 *
 * ## Why the VALUE is forwarded here
 *
 * `reportSchemaMismatch.ts` never forwards received values, because on every
 * issue code except `invalid_type` the `received` field is real API data. This
 * helper deliberately forwards the value, and the distinction is the point: an
 * enum member is a TYPE CODE — part of the API's structure, drawn from a small
 * closed vocabulary the backend defines — not user- or customer-supplied data.
 * It is also the entire content of the report: "Contract.type is not a declared
 * member" is unactionable without knowing whether the value is `''` (a
 * never-set column) or `Full Service` (an unmigrated import). Nothing else in
 * the payload distinguishes those.
 *
 * This is a narrow, code-specific exception and does NOT loosen that file-wide
 * rule. Values of other issue codes are still never forwarded, on either path.
 * The exception holds only for a value the schema itself proves is an enum
 * member position; do not read it as licence to attach `received` elsewhere.
 *
 * ## Volume
 *
 * `/contracts` renders many rows through one schema, so a naive report fires
 * once per row per render. Reporting is deduped on `kind|path|value` with the
 * same discipline as `reportSchemaMismatch`: the first occurrence of a distinct
 * triple is always reported, repeats are sampled, and the tracking set is
 * bounded so a pathological session cannot grow it forever.
 *
 * Grouping is pinned to its own `fingerprint` root (`api-schema-unknown-enum`)
 * so these file separately from `api-schema-mismatch` and leave that issue's
 * existing Sentry grouping undisturbed.
 */
import { captureMessage } from '@sentry/react';
import { z } from 'zod';

/** Distinct `kind|path|value` triples already reported this session. */
const seenFingerprints = new Set<string>();

/** Bound on `seenFingerprints` so a pathological session cannot grow it forever. */
const MAX_TRACKED_FINGERPRINTS = 500;

/**
 * Fraction of REPEAT unknown values forwarded. The first occurrence of each
 * distinct triple is always reported, so this governs volume, not coverage.
 * Matched to the `relaxed` mismatch rate: an unknown enum is likewise a value
 * the boundary absorbed successfully rather than a failure it could not.
 */
const DEFAULT_SAMPLE_RATE = 0.05;
let unknownEnumSampleRate = DEFAULT_SAMPLE_RATE;

/** Override the repeat sample rate (test hook / runtime tuning). */
export function configureUnknownEnumSampling(rate: number): void {
  unknownEnumSampleRate = rate;
}

/**
 * Always report a triple the session has not seen; sample the repeats.
 *
 * Once the tracking set is full we can no longer tell new from repeat, so we
 * sample rather than report — an untracked triple looks new on EVERY occurrence
 * and would otherwise forward every time, flooding the issue this budget exists
 * to protect.
 */
function shouldReport(fingerprint: string): boolean {
  if (seenFingerprints.has(fingerprint)) {
    return Math.random() < unknownEnumSampleRate;
  }

  if (seenFingerprints.size >= MAX_TRACKED_FINGERPRINTS) {
    return Math.random() < unknownEnumSampleRate;
  }

  seenFingerprints.add(fingerprint);
  return true;
}

/**
 * Cap on the forwarded value. An enum member is short by construction; a long
 * one is itself the corruption, and the prefix says so without shipping a blob.
 */
const MAX_REPORTED_VALUE_LENGTH = 120;

/** Where the unknown value was seen, for grouping and for the reader. */
export interface TolerantEnumContext {
  /** The `parseApiResponse` kind, e.g. `Contract`. */
  kind: string;
  /** Field path within that schema, e.g. `type`. */
  path: string;
}

function reportUnknownEnumValue(kind: string, path: string, value: string): void {
  if (!shouldReport(`${kind}|${path}|${value}`)) {
    return;
  }

  const reportedValue = value.slice(0, MAX_REPORTED_VALUE_LENGTH);

  console.warn(
    `${kind}.${path}: value outside the declared enum, accepted verbatim — ${JSON.stringify(
      reportedValue,
    )}`,
  );

  captureMessage(`API schema unknown enum value: ${kind}.${path}`, {
    level: 'warning',
    // Its own grouping root, so this never merges into `api-schema-mismatch`.
    // Deliberately NOT keyed on the value: one drifted column is one issue, and
    // the distinct values it holds ride along in `extra`.
    fingerprint: ['api-schema-unknown-enum', kind, path],
    tags: {
      'api.kind': kind,
      'api.path': path,
    },
    extra: {
      kind,
      path,
      // A type code, not user data — see the module docblock.
      value: reportedValue,
      outcome: 'accepted verbatim by the read schema; no raw-tier fallback',
    },
  });
}

/**
 * Accept `strict`'s members, or any other string — reporting the ones that fall
 * through. The value is returned unchanged either way.
 *
 * `Schema` is constrained to STRING ENUM positions, not `ZodTypeAny`. That
 * constraint is what makes the value-forwarding exception above safe: it is the
 * compiler, not this comment, that stops someone passing a free-text field (a
 * name, an email) and shipping user data to Sentry by construction.
 *
 * `ZodNativeEnum` is deliberately NOT accepted. `.or()` is typed against `this`,
 * which collapses to this constraint rather than the instantiated `Schema`, so
 * admitting it would widen every caller's output to `string | number` (a TS
 * enum may hold numeric members). Every generated SDK enum is `zod.enum([...])`,
 * so nothing needs it; add it only alongside a fix for that widening.
 *
 * Ordering is load-bearing: zod's union returns the FIRST option that parses,
 * so a declared member matches `strict` and never reaches the reporting branch.
 * That silence guarantee holds for SYNC parsing only — `parseAsync`/
 * `safeParseAsync` evaluate every option via `Promise.all`, which would run the
 * reporting branch for valid members too. Safe today because `parseApiResponse`
 * parses synchronously; if a schema using this ever feeds a form, note that
 * `zodResolver` parses async and would report on every valid row.
 */
export function tolerantEnum<Schema extends z.ZodEnum<[string, ...string[]]>>(
  strict: Schema,
  { kind, path }: TolerantEnumContext,
) {
  return strict.or(
    z.string().transform((value) => {
      reportUnknownEnumValue(kind, path, value);
      return value;
    }),
  );
}

/** Reset the session dedupe state and sample rate. Test hook. */
export function resetUnknownEnumReporting(): void {
  seenFingerprints.clear();
  unknownEnumSampleRate = DEFAULT_SAMPLE_RATE;
}
